/**
 * ランキングのシーズン（#284、[ADR-013](../../../../docs/adr/013-seasons-as-date-ranges.md)）。
 *
 * **1 シーズン = 日本時間の暦の 1 か月**（毎月 1 日 0:00 から、翌月 1 日 0:00 の手前まで）。日本だけの提供なので
 * 日本時間で切る（ADR-023）。シーズンは**日付で絞った集計**で、どこにも保存しない（リセットの処理も無い）。
 *
 * **区切りの計算はここだけに置く。** API と公開サイトが使う。アプリ（Swift）は計算せず、API の応答の
 * `startsAt` / `endsAt` を使う（端末の時計や時間帯に左右されない）。
 */
/** シーズンの ID の形（`YYYY-MM`）。 */
export const SEASON_ID_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
/**
 * 日本時間と UTC の差。**日本には夏時間が無い**ので、いつも +9 時間で足りる。
 * `Intl` の時間帯を使わないのは、Workers と Node とブラウザで同じ結果を、表を引かずに出すため。
 */
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
/** その時刻が入るシーズンの ID（`YYYY-MM`）。 */
export function seasonOf(date) {
    // 日本時間の暦に寄せてから、UTC の年月として読む。
    const jst = new Date(date.getTime() + JST_OFFSET_MS);
    return formatSeasonId(jst.getUTCFullYear(), jst.getUTCMonth() + 1);
}
/**
 * シーズンの範囲。`endsAt` は**含まない**（次のシーズンの始まり）。どちらも `toISOString()` の形で、
 * `tournaments.created_at` と文字列のまま比べられる（`newTournamentRow` が同じ形で書く）。
 *
 * @throws {RangeError} ID の形が違う（呼ぶ前に `seasonIdSchema` で確かめる）
 */
export function seasonRange(id) {
    const { year, month } = parseSeasonId(id);
    return {
        // `Date.UTC` は 12 月の次（month = 12）を翌年の 1 月に繰り上げる。
        startsAt: new Date(Date.UTC(year, month - 1, 1) - JST_OFFSET_MS).toISOString(),
        endsAt: new Date(Date.UTC(year, month, 1) - JST_OFFSET_MS).toISOString(),
    };
}
/**
 * 前のシーズンの ID（`2026-01` の前は `2025-12`）。公開サイトの「せんげつ」へのリンクに使う。
 *
 * @throws {RangeError} ID の形が違う
 */
export function previousSeason(id) {
    const { year, month } = parseSeasonId(id);
    return month === 1 ? formatSeasonId(year - 1, 12) : formatSeasonId(year, month - 1);
}
function parseSeasonId(id) {
    if (!SEASON_ID_PATTERN.test(id)) {
        throw new RangeError(`シーズンの ID の形が違います: ${id}`);
    }
    const [year, month] = id.split('-').map(Number);
    return { year, month };
}
function formatSeasonId(year, month) {
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;
}
